import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, Clock, MapPin, User, Phone, Mail, CreditCard,
  CalendarDays, AlertTriangle, GripVertical, ChevronRight,
  Filter, Settings, Loader2, X, Check, Edit3, ExternalLink, UserCog, Plus,
} from 'lucide-react';
import { AdminSubNav, SUBNAV_SCHEDULE } from '../../components/admin/AdminSubNav';
import AddNewCleanDialog from '../../components/admin/AddNewCleanDialog';
import BlockTimeDialog from '../../components/admin/BlockTimeDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  'Booked': { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
  'Confirmed': { bg: '#dcfce7', border: '#22c55e', text: '#166534' },
  'In Progress': { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
  'Service Finished': { bg: '#e0e7ff', border: '#6366f1', text: '#3730a3' },
  'Admin Confirmed': { bg: '#fce7f3', border: '#ec4899', text: '#9d174d' },
  'Completed': { bg: '#f3f4f6', border: '#9ca3af', text: '#4b5563' },
  'Pending QC': { bg: '#fff7ed', border: '#F56F05', text: '#9a3412' },
};

const PAYMENT_BADGE = {
  'Paid': { class: 'bg-emerald-100 text-emerald-700', label: 'Paid' },
  'Draft': { class: 'bg-zinc-100 text-zinc-600', label: 'Pending' },
  'Ready for Charge': { class: 'bg-amber-100 text-amber-700', label: 'Ready' },
  'Payment Failed': { class: 'bg-red-100 text-red-700', label: 'Failed' },
};

export default function AdminSchedulePage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/dashboard');
  const calendarRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [services, setServices] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Filters
  const [filterServiceArea, setFilterServiceArea] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [filterGuru, setFilterGuru] = useState(() => new URLSearchParams(window.location.search).get('guru') || 'all');
  const [team, setTeam] = useState([]);
  const chooseGuru = (v) => {
    setFilterGuru(v);
    const u = new URL(window.location.href);
    if (v === 'all') u.searchParams.delete('guru'); else u.searchParams.set('guru', v);
    window.history.replaceState(null, '', u);
  };
  // Drag-select on the calendar → lightweight New Appointment dialog
  const [dragCreateOpen, setDragCreateOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockEdit, setBlockEdit] = useState(null);
  const [dragRange, setDragRange] = useState({ start: null, end: null });
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    const current = api.view?.type;
    if (isMobile && current !== 'listWeek' && current !== 'dayGridMonth' && current !== 'timeGridDay') {
      api.changeView('listWeek');
    }
    if (!isMobile && current === 'listWeek') {
      api.changeView('timeGridWeek');
    }
  }, [isMobile]);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/schedule/config`, { headers });
      setConfig(res.data);
    } catch (_) { /* config fetch failed */ }
  }, [headers]);

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/services`, { headers });
      setServices(res.data);
    } catch (_) { /* services fetch failed */ }
  }, [headers]);

  const fetchServiceAreas = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/service-areas`, { headers });
      setServiceAreas(res.data);
    } catch (_) { /* service areas fetch failed */ }
  }, [headers]);

  useEffect(() => {
    fetchConfig();
    fetchServices();
    fetchServiceAreas();
  }, [fetchConfig, fetchServices, fetchServiceAreas]);

  const fetchEvents = useCallback((fetchInfo, successCallback, failureCallback) => {
    setLoading(true);
    const params = new URLSearchParams({
      start: fetchInfo.startStr,
      end: fetchInfo.endStr,
    });
    if (filterServiceArea !== 'all') params.append('service_area_id', filterServiceArea);
    if (filterStatus !== 'all') params.append('status', filterStatus);
    if (filterService !== 'all') params.append('service_id', filterService);
    if (filterPayment !== 'all') params.append('payment_status', filterPayment);
    if (filterGuru !== 'all') params.append('guru_user_id', filterGuru);

    Promise.all([
      axios.get(`${API_URL}/api/schedule/calendar?${params}`, { headers }),
      axios.get(`${API_URL}/api/admin/schedule/blocks`, { headers, params: { start: fetchInfo.startStr, end: fetchInfo.endStr } }),
    ])
      .then(([res, blk]) => {
        setTeam(blk.data.team || []);
        const blocks = blk.data.blocks.filter(b => filterGuru === 'all' || b.scope === 'business' || b.cleaner_user_id === filterGuru).map(b => ({
          id: `block-${b.id}`, title: b.label, start: b.start_time, end: b.end_time, editable: false,
          backgroundColor: '#e7e5dc', borderColor: '#8a8c80', textColor: '#3f4038', classNames: ['gg-block-event'],
          extendedProps: { kind: 'block', block: b },
        }));
        const mapped = res.data.map(ev => {
          const colors = STATUS_COLORS[ev.status] || STATUS_COLORS['Booked'];
          return {
            id: ev.id,
            title: ev.title,
            start: ev.start,
            end: ev.end,
            backgroundColor: colors.bg,
            borderColor: colors.border,
            textColor: colors.text,
            extendedProps: ev,
          };
        });
        successCallback([...mapped, ...blocks]);
        setLoading(false);
      })
      .catch(err => {
        toast.error('Failed to load schedule');
        failureCallback(err);
        setLoading(false);
      });
  }, [headers, filterServiceArea, filterStatus, filterService, filterPayment, filterGuru]);

  // Refetch when filters change
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (api) api.refetchEvents();
  }, [filterServiceArea, filterStatus, filterService, filterPayment, filterGuru]);

  const handleEventClick = (info) => {
    if (info.event.extendedProps.kind === 'block') { setBlockEdit(info.event.extendedProps.block); setBlockOpen(true); return; }
    setSelectedEvent(info.event.extendedProps);
    setEditingField(null);
    setEditValues({});
    setPanelOpen(true);
  };

  const flashConflict = (info) => {
    // Briefly flash the reverted event red so the admin sees WHY it snapped back.
    // Also flash any events at the target time that block the move so admins can see the conflict.
    try {
      const targetStart = info.event.start ? new Date(info.event.start).getTime() : null;
      const targetEnd = info.event.end ? new Date(info.event.end).getTime() : null;
      const draggedId = info.event.id;
      // Flash the dragged event
      if (info.el) {
        info.el.classList.add('conflict-flash');
        setTimeout(() => info.el && info.el.classList.remove('conflict-flash'), 1400);
      }
      // Flash overlapping events (best-effort — visible in Week/Day views)
      const api = calendarRef.current && calendarRef.current.getApi();
      if (api && targetStart != null && targetEnd != null) {
        api.getEvents().forEach(ev => {
          if (ev.id === draggedId) return;
          const s = ev.start ? ev.start.getTime() : null;
          const e = ev.end ? ev.end.getTime() : null;
          if (s != null && e != null && s < targetEnd && e > targetStart) {
            const els = document.querySelectorAll(`[data-testid="cal-event-${ev.id}"]`);
            els.forEach(el => {
              const root = el.closest('.fc-event') || el;
              root.classList.add('conflict-flash-other');
              setTimeout(() => root.classList.remove('conflict-flash-other'), 1400);
            });
          }
        });
      }
    } catch (err) { /* best-effort visual, never crash */ }
  };

  const handleEventDrop = async (info) => {
    const ev = info.event.extendedProps;
    const newStart = info.event.start.toISOString();

    try {
      // Validate first
      const valRes = await axios.post(`${API_URL}/api/schedule/validate`, {
        service_id: ev.service_id,
        arrival_start_datetime: newStart,
        cleaner_user_id: ev.cleaner_user_id,
        exclude_appointment_id: ev.id,
        admin_override: false,
      }, { headers });

      if (!valRes.data.valid) {
        const errs = valRes.data.errors;
        const overridable = errs.every(e => e.overridable);
        const messages = errs.map(e => e.message).join('\n');

        if (overridable) {
          const needsReason = errs.some(e => e.requires_reason);
          const reason = needsReason ? window.prompt(`${messages}\n\nEnter a reason to book over the blocked time:`) : (window.confirm(`${messages}\n\nDo you want to use admin override?`) ? 'ok' : null);
          if (reason && reason.trim()) {
            await axios.post(`${API_URL}/api/schedule/reschedule`, {
              appointment_id: ev.id,
              new_arrival_start_datetime: newStart,
              admin_override: true,
              override_reason: needsReason ? reason.trim() : null,
            }, { headers });
            toast.success('Rescheduled with admin override');
            return;
          }
        } else {
          toast.error(messages);
        }
        flashConflict(info);
        info.revert();
        return;
      }

      // Apply reschedule
      await axios.post(`${API_URL}/api/schedule/reschedule`, {
        appointment_id: ev.id,
        new_arrival_start_datetime: newStart,
        admin_override: false,
      }, { headers });
      toast.success('Appointment rescheduled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reschedule');
      flashConflict(info);
      info.revert();
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/appointments/${appointmentId}`, { status: newStatus }, { headers });
      toast.success(`Status changed to ${newStatus}`);
      setSelectedEvent(prev => ({ ...prev, status: newStatus }));
      const api = calendarRef.current?.getApi();
      if (api) api.refetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleSaveField = async (appointmentId, field, value) => {
    try {
      const body = {};
      body[field] = value;
      await axios.put(`${API_URL}/api/schedule/appointments/${appointmentId}`, null, {
        headers,
        params: body,
      });
      toast.success('Updated successfully');
      setEditingField(null);
      setSelectedEvent(prev => ({ ...prev, [field]: value }));
      const api = calendarRef.current?.getApi();
      if (api) api.refetchEvents();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const renderEventContent = (eventInfo) => {
    const ev = eventInfo.event.extendedProps;
    if (ev.kind === 'block') return (
      <div className="p-1 text-xs leading-tight overflow-hidden cursor-pointer w-full" data-testid={`cal-block-${ev.block.id}`}>
        <div className="font-semibold truncate">⊘ {eventInfo.event.title}</div>
        <div className="opacity-75 truncate">{ev.block.cleaner_name || 'Whole business'} · Unavailable</div>
      </div>
    );
    const colors = STATUS_COLORS[ev.status] || STATUS_COLORS['Booked'];
    const arrStart = ev.arrival_start ? new Date(ev.arrival_start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Los_Angeles' }) : '';
    const arrEnd = ev.arrival_end ? new Date(ev.arrival_end).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Los_Angeles' }) : '';
    const cleanerName = ev.assigned_cleaner_name || ev.cleaner_name || '';
    const cleanerInitials = cleanerName ? cleanerName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() : '';

    return (
      <div className="p-1 text-xs leading-tight overflow-hidden cursor-pointer w-full border-l-2 rounded-sm" style={{ borderLeftColor: colors.border }} data-testid={`cal-event-${ev.id}`}>
        <div className="flex items-center gap-1.5 mb-0.5">
          {cleanerInitials ? (
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-white text-[8px] font-bold border border-zinc-300 text-zinc-700 flex-shrink-0" title={cleanerName}>
              {cleanerInitials}
            </span>
          ) : (
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-zinc-100 text-[8px] text-zinc-400 flex-shrink-0" title="Unassigned">?</span>
          )}
          <span className="font-semibold truncate">{eventInfo.event.title}</span>
        </div>
        <div className="opacity-80 truncate">
          {arrStart}–{arrEnd}
        </div>
        <div className="opacity-70 truncate">{ev.service_name}</div>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.border }} />
          <span className="truncate">{ev.status}</span>
          {ev.payment_status === 'Payment Failed' && (
            <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
          )}
        </div>
      </div>
    );
  };

  const handleDateClick = (info) => {
    // Explicit click on a day/slot → Add New Clean with that date (and time, in time-grid views) prefilled
    const start = info.allDay ? new Date(info.date.getFullYear(), info.date.getMonth(), info.date.getDate(), 9, 0) : info.date;
    setDragRange({ start: start.toISOString(), end: null });
    setDragCreateOpen(true);
  };

  const handleDateSelect = (info) => {
    // Drag-select a range → Add New Clean with start AND end prefilled
    setDragRange({ start: info.start.toISOString(), end: info.allDay ? null : info.end.toISOString() });
    setDragCreateOpen(true);
    if (calendarRef.current) {
      try { calendarRef.current.getApi().unselect(); } catch (e) { /* noop */ }
    }
  };

  const openAddNewClean = () => { setDragRange({ start: null, end: null }); setDragCreateOpen(true); };

  const statuses = ['Booked', 'Confirmed', 'In Progress', 'Service Finished', 'Admin Confirmed', 'Completed'];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-schedule-page">
      <AdminSubNav section="Schedule" items={SUBNAV_SCHEDULE} />
      {/* Header */}
      <div className="gg-legacy-head bg-white border-b border-zinc-200 sticky top-12 md:top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between flex-wrap gap-y-2 gap-x-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={back} data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-zinc-900">Calendar</h1>
                <p className="text-xs text-zinc-500">
                  {config?.is_solo_mode ? 'Solo Mode' : `${config?.active_cleaner_count || 0} Active Gurus`}
                  {' '} &middot; America/Los_Angeles
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {loading && <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />}
              <Button variant="outline" onClick={() => { setBlockEdit(null); setBlockOpen(true); }} className="h-9 px-3" data-testid="block-time-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Block time
              </Button>
              <Button onClick={openAddNewClean} className="bg-[#F56F05] hover:bg-[#C2410C] text-white font-bold h-9 px-3 sm:px-4" data-testid="add-new-clean-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add New Clean
              </Button>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="flex flex-wrap items-center gap-2 mt-3 pb-1" data-testid="schedule-filter-bar">
            <Filter className="w-4 h-4 text-zinc-400" />
            <Select value={filterServiceArea} onValueChange={setFilterServiceArea}>
              <SelectTrigger className="w-[calc(50%-0.5rem)] sm:w-[150px] h-8 text-xs" data-testid="filter-service-area">
                <SelectValue placeholder="Service Area" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Areas</SelectItem>
                {serviceAreas.map(sa => (
                  <SelectItem key={sa.id} value={sa.id}>{sa.city_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[calc(50%-0.5rem)] sm:w-[140px] h-8 text-xs" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterService} onValueChange={setFilterService}>
              <SelectTrigger className="w-[calc(50%-0.5rem)] sm:w-[160px] h-8 text-xs" data-testid="filter-service-type">
                <SelectValue placeholder="Service Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Services</SelectItem>
                {services.filter(s => s.is_active).map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPayment} onValueChange={setFilterPayment}>
              <SelectTrigger className="w-[calc(50%-0.5rem)] sm:w-[130px] h-8 text-xs" data-testid="filter-payment">
                <SelectValue placeholder="Payment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="OK">Paid</SelectItem>
                <SelectItem value="Failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterGuru} onValueChange={chooseGuru}>
              <SelectTrigger className="w-[calc(50%-0.5rem)] sm:w-[160px] h-8 text-xs" data-testid="filter-guru">
                <SelectValue placeholder="Guru" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Gurus</SelectItem>
                {filterGuru !== 'all' && !team.some(t => t.id === filterGuru) && <SelectItem value={filterGuru}>Selected Guru</SelectItem>}
                {team.map(t => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
            {(filterServiceArea !== 'all' || filterStatus !== 'all' || filterService !== 'all' || filterPayment !== 'all' || filterGuru !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-zinc-500"
                onClick={() => { setFilterServiceArea('all'); setFilterStatus('all'); setFilterService('all'); setFilterPayment('all'); chooseGuru('all'); }}
                data-testid="clear-filters"
              >
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <style>{`
          .fc .fc-toolbar.fc-header-toolbar { flex-wrap: wrap; gap: 0.5rem; }
          .fc .fc-toolbar-chunk { display: flex; flex-wrap: wrap; gap: 0.25rem; }
          .fc .gg-block-event { background-image: repeating-linear-gradient(135deg, rgba(0,0,0,.06) 0 6px, transparent 6px 12px); }
        `}</style>
        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="p-2 sm:p-4 overflow-x-auto">
            <FullCalendar
              firstDay={1}
              slotEventOverlap={false}
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, momentTimezonePlugin]}
              initialView={isMobile ? 'listWeek' : 'timeGridWeek'}
              headerToolbar={isMobile ? {
                left: 'prev,next',
                center: 'title',
                right: 'today'
              } : {
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
              }}
              footerToolbar={isMobile ? {
                center: 'dayGridMonth,timeGridDay,listWeek'
              } : false}
              buttonText={{
                today: 'Today',
                month: 'Month',
                week: 'Week',
                day: 'Day',
                list: 'Agenda',
              }}
              events={fetchEvents}
              eventContent={renderEventContent}
              eventClick={handleEventClick}
              dateClick={handleDateClick}
              selectable={true}
              select={handleDateSelect}
              unselectAuto={true}
              editable={true}
              droppable={false}
              eventDrop={handleEventDrop}
              eventDurationEditable={false}
              slotMinTime="06:00:00"
              slotMaxTime="24:00:00"
              slotDuration="00:30:00"
              allDaySlot={false}
              nowIndicator={true}
              timeZone="America/Los_Angeles"
              height="auto"
              contentHeight={isMobile ? 550 : 650}
              dayMaxEvents={isMobile ? 2 : 4}
              weekends={true}
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5, 6],
                startTime: '08:00',
                endTime: '18:00',
              }}
              eventOverlap={false}
              slotLabelFormat={{
                hour: 'numeric',
                minute: '2-digit',
                meridiem: 'short',
              }}
              dayHeaderFormat={{
                weekday: 'short',
                month: 'numeric',
                day: 'numeric',
              }}
              eventTimeFormat={{
                hour: 'numeric',
                minute: '2-digit',
                meridiem: 'short',
              }}
            />
          </CardContent>
        </Card>
      </div>

      {/* Event Detail Panel */}
      <Sheet open={panelOpen} onOpenChange={setPanelOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="event-detail-panel">
          {selectedEvent && <EventDetailPanel
            event={selectedEvent}
            onStatusChange={handleStatusChange}
            onSaveField={handleSaveField}
            editingField={editingField}
            setEditingField={setEditingField}
            editValues={editValues}
            setEditValues={setEditValues}
            navigate={navigate}
          />}
        </SheetContent>
      </Sheet>

      <BlockTimeDialog open={blockOpen} onOpenChange={setBlockOpen} block={blockEdit} headers={headers}
        onSaved={() => { try { calendarRef.current.getApi().refetchEvents(); } catch (e) { /* noop */ } }} />
      <AddNewCleanDialog
        open={dragCreateOpen}
        onOpenChange={setDragCreateOpen}
        defaultStart={dragRange.start}
        defaultEnd={dragRange.end}
        headers={headers}
        onCreated={() => {
          if (calendarRef.current) {
            try { calendarRef.current.getApi().refetchEvents(); } catch (e) { /* noop */ }
          }
        }}
      />
    </div>
  );
}

function EventDetailPanel({ event, onStatusChange, onSaveField, editingField, setEditingField, editValues, setEditValues, navigate }) {
  const ev = event;
  const colors = STATUS_COLORS[ev.status] || STATUS_COLORS['Booked'];
  const payInfo = PAYMENT_BADGE[ev.payment_status] || PAYMENT_BADGE['Draft'];

  const formatTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZone: 'America/Los_Angeles'
    });
  };

  const statusFlow = ['Booked', 'Confirmed', 'In Progress', 'Service Finished', 'Admin Confirmed', 'Completed'];
  const currentIdx = statusFlow.indexOf(ev.status);
  const nextStatuses = currentIdx >= 0 && currentIdx < statusFlow.length - 1
    ? [statusFlow[currentIdx + 1]]
    : [];

  return (
    <div className="space-y-5 pt-2" data-testid="event-detail-content">
      <SheetHeader>
        <SheetTitle className="text-lg">{ev.client_name}</SheetTitle>
      </SheetHeader>

      {/* Status & Service */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge style={{ backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }} className="border text-xs" data-testid="event-status-badge">
          {ev.status}
        </Badge>
        <Badge variant="outline" className="text-xs">{ev.service_name}</Badge>
        <Badge className={`text-xs ${payInfo.class}`} data-testid="event-payment-badge">{payInfo.label}</Badge>
      </div>

      {/* Arrival Window */}
      <div className="bg-zinc-50 rounded-lg p-3 space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-zinc-700">
          <Clock className="w-4 h-4 text-[#F56F05]" />
          Arrival Window
        </div>
        <div className="text-sm text-zinc-900" data-testid="event-arrival-window">
          {formatTime(ev.arrival_start)} — {new Date(ev.arrival_end).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Los_Angeles' })}
        </div>
        <div className="flex gap-4 text-xs text-zinc-500">
          <span>Est: {ev.estimated_minutes}m</span>
          <span>Buffer: {ev.buffer_minutes}m</span>
          <span>Total: {ev.estimated_minutes + ev.buffer_minutes}m</span>
        </div>
      </div>

      {/* Client Info */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-zinc-700 flex items-center gap-2">
          <User className="w-4 h-4" /> Client Info
        </h3>
        <div className="text-sm space-y-1 pl-6">
          <div className="flex items-center gap-2">
            <Mail className="w-3 h-3 text-zinc-400" />
            <span className="text-zinc-700">{ev.client_email || '—'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-3 h-3 text-zinc-400" />
            <span className="text-zinc-700">{ev.client_phone || '—'}</span>
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-zinc-700 flex items-center gap-2">
          <MapPin className="w-4 h-4" /> Address
        </h3>
        <div className="text-sm pl-6">
          <p className="text-zinc-700">{ev.address_line || '—'}</p>
          {ev.access_notes && (
            <p className="text-xs text-zinc-500 mt-1 italic">Access: {ev.access_notes}</p>
          )}
        </div>
      </div>

      {/* Assigned Team */}
      {(ev.team && ev.team.length > 0) ? (
        <div className="space-y-1.5">
          <h3 className="text-sm font-medium text-zinc-700 flex items-center gap-2">
            <UserCog className="w-4 h-4" /> Assigned Team
            {ev.team_count > 1 && (
              <span className="text-[10px] font-bold text-[#F56F05] bg-orange-50 px-1.5 py-0.5 rounded-full">
                {ev.team_count} gurus
              </span>
            )}
          </h3>
          <div className="pl-6 flex flex-wrap gap-1.5" data-testid="calendar-team">
            {ev.team.map(m => (
              <span key={m.user_id}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  m.is_lead ? 'bg-[#F56F05] text-white' : 'bg-zinc-100 text-zinc-700'
                }`}
                data-testid={`calendar-team-${m.user_id}`}>
                {m.is_lead && <span className="text-[9px] font-bold opacity-80">LEAD</span>}
                {m.name}
              </span>
            ))}
          </div>
        </div>
      ) : ev.cleaner_name ? (
        <div className="space-y-1">
          <h3 className="text-sm font-medium text-zinc-700">Assigned Team</h3>
          <p className="text-sm text-zinc-700 pl-6">{ev.cleaner_name}</p>
        </div>
      ) : null}

      {/* Internal Notes */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-zinc-700">Internal Notes</h3>
          <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => {
            setEditingField('notes_internal');
            setEditValues(prev => ({ ...prev, notes_internal: ev.notes_internal || '' }));
          }}>
            <Edit3 className="w-3 h-3 mr-1" /> Edit
          </Button>
        </div>
        {editingField === 'notes_internal' ? (
          <div className="space-y-2">
            <Textarea
              value={editValues.notes_internal || ''}
              onChange={e => setEditValues(prev => ({ ...prev, notes_internal: e.target.value }))}
              className="text-sm"
              rows={3}
              data-testid="edit-notes-internal"
            />
            <div className="flex gap-2">
              <Button size="sm" className="h-7 text-xs bg-[#F56F05] hover:bg-[#e06505]" onClick={() => onSaveField(ev.id, 'notes_internal', editValues.notes_internal)}>
                <Check className="w-3 h-3 mr-1" /> Save
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingField(null)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-zinc-500 pl-6">{ev.notes_internal || 'No notes'}</p>
        )}
      </div>

      {/* Status Actions */}
      <div className="border-t border-zinc-200 pt-4 space-y-2">
        <h3 className="text-sm font-medium text-zinc-700">Actions</h3>
        <div className="flex flex-wrap gap-2">
          {nextStatuses.map(s => (
            <Button
              key={s}
              size="sm"
              className="text-xs bg-[#F56F05] hover:bg-[#e06505]"
              onClick={() => onStatusChange(ev.id, s)}
              data-testid={`status-change-${s.replace(/\s+/g, '-').toLowerCase()}`}
            >
              Move to {s}
            </Button>
          ))}
          {ev.status !== 'Canceled' && ev.status !== 'Completed' && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => {
                if (window.confirm('Cancel this appointment?')) {
                  onStatusChange(ev.id, 'Canceled');
                }
              }}
              data-testid="cancel-appointment-btn"
            >
              Cancel
            </Button>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs mt-2"
          onClick={() => navigate(`/admin/appointments/${ev.id}`)}
          data-testid="view-full-detail-btn"
        >
          View Full Detail <ExternalLink className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </div>
  );
}
